import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CreditCard, Plus, Edit, Trash2, Users, Star, IndianRupee, DollarSign, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultForm = {
  name: '', slug: '', tagline: '', users: 10, storage_gb: 5,
  price_monthly: 0, price_yearly: 0,
  price_monthly_inr: 0, price_yearly_inr: 0,
  requests_per_minute: 100, requests_per_hour: 1000, requests_per_day: 5000,
  min_users: '5', price_unit: 'recruiter / month', platform_fee: '',
  features: '', highlighted: false, is_active: true, sort_order: 0
};

export default function PlanManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [displayCurrency, setDisplayCurrency] = useState('INR');

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/plans/admin`, { headers: getHeaders() });
      setPlans(res.data);
    } catch { toast.error('Failed to load plans'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        features: typeof form.features === 'string' ? form.features.split('\n').filter(f => f.trim()) : form.features,
        users: parseInt(form.users) || -1,
        storage_gb: parseInt(form.storage_gb) || -1,
        price_monthly: parseFloat(form.price_monthly) || 0,
        price_yearly: parseFloat(form.price_yearly) || 0,
        price_monthly_inr: parseFloat(form.price_monthly_inr) || 0,
        price_yearly_inr: parseFloat(form.price_yearly_inr) || 0,
      };
      if (editPlan) {
        await axios.put(`${API}/plans/admin/${editPlan.slug}`, payload, { headers: getHeaders() });
        toast.success('Plan updated');
      } else {
        await axios.post(`${API}/plans/admin`, payload, { headers: getHeaders() });
        toast.success('Plan created');
      }
      setShowDialog(false);
      setEditPlan(null);
      setForm(defaultForm);
      fetchPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete plan "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/plans/admin/${slug}`, { headers: getHeaders() });
      toast.success('Plan deleted');
      fetchPlans();
    } catch { toast.error('Delete failed'); }
  };

  const openEdit = (plan) => {
    setEditPlan(plan);
    setForm({
      ...plan,
      features: Array.isArray(plan.features) ? plan.features.join('\n') : plan.features || '',
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditPlan(null);
    setForm(defaultForm);
    setShowDialog(true);
  };

  const getPrice = (plan) => displayCurrency === 'INR' ? plan.price_monthly_inr : plan.price_monthly;
  const getSymbol = () => displayCurrency === 'INR' ? '\u20B9' : '$';

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div data-testid="plan-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-white flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <CreditCard size={24} className="text-violet-400" />Subscription Plans
          </h1>
          <p className="text-sm text-slate-400 mt-1">Manage pricing tiers and features — changes reflect on the website</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-800 rounded-lg p-0.5 border border-slate-700">
            <button data-testid="currency-inr" onClick={() => setDisplayCurrency('INR')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${displayCurrency === 'INR' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
              <IndianRupee size={12} />INR
            </button>
            <button data-testid="currency-usd" onClick={() => setDisplayCurrency('USD')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${displayCurrency === 'USD' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
              <DollarSign size={12} />USD
            </button>
          </div>
          <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-500 text-white font-bold" data-testid="create-plan-btn">
            <Plus size={16} className="mr-1.5" />Create Plan
          </Button>
        </div>
      </div>

      {/* Plan Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="plans-grid">
        {plans.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map(plan => (
          <div key={plan.slug} data-testid={`plan-card-${plan.slug}`}
            className={`relative bg-slate-800/50 border rounded-xl p-6 transition-all hover:border-slate-600
              ${plan.highlighted ? 'border-violet-500 ring-1 ring-violet-500/30' : 'border-slate-700'}`}>
            {plan.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-violet-600 text-white text-[10px] font-bold px-3">
                  <Star size={10} className="mr-1" />Most Popular
                </Badge>
              </div>
            )}

            <div className="mb-4">
              <h3 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{plan.name}</h3>
              {plan.tagline && <p className="text-xs text-slate-400 mt-0.5">{plan.tagline}</p>}
              <p className="text-[10px] text-slate-500">Slug: {plan.slug}</p>
            </div>

            <div className="mb-4">
              <span className="text-3xl font-extrabold text-violet-400">{getSymbol()}{getPrice(plan).toLocaleString()}</span>
              <span className="text-sm text-slate-500 ml-1">/ {plan.price_unit || 'month'}</span>
            </div>

            {plan.min_users && (
              <p className="text-xs text-slate-400 mb-1 flex items-center gap-1.5">
                <Users size={12} />Min {plan.min_users} users
              </p>
            )}

            <div className="text-xs text-slate-500 mb-4 space-y-0.5">
              <p>{getSymbol()}{getPrice(plan).toLocaleString()}/mo | {displayCurrency === 'INR' ? `$${plan.price_monthly}` : `\u20B9${plan.price_monthly_inr.toLocaleString()}`}/mo</p>
            </div>

            {/* Tenant count */}
            <p className="text-xs text-slate-500 mb-3 pb-3 border-b border-slate-700">
              {plan.tenant_count || 0} tenant(s) on this plan
            </p>

            {/* Features */}
            <ul className="space-y-1.5 mb-5">
              {(plan.features || []).map(f => (
                <li key={f} className="flex items-start gap-2 text-xs text-slate-300">
                  <Check size={12} className="text-violet-400 shrink-0 mt-0.5" />{f}
                </li>
              ))}
            </ul>

            {!plan.is_active && (
              <Badge variant="outline" className="border-red-500/50 text-red-400 text-[10px] mb-3">Inactive</Badge>
            )}

            <div className="flex gap-2 pt-3 border-t border-slate-700">
              <Button variant="outline" size="sm" onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.slug}`}
                className="flex-1 border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 font-bold text-xs">
                <Edit size={13} className="mr-1.5" />Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleDelete(plan.slug)} data-testid={`delete-plan-${plan.slug}`}
                className="border-red-500/40 text-red-400 hover:text-red-300 hover:bg-red-500/10 font-bold text-xs">
                <Trash2 size={13} className="mr-1" />Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-white">
              {editPlan ? `Edit ${editPlan.name}` : 'Create New Plan'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-slate-400">Plan Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="plan-name-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-400">Slug</Label>
                <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} disabled={!!editPlan}
                  className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="plan-slug-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-400">Tagline</Label>
              <Input value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })}
                placeholder="e.g., Scale recruiter revenue" className="mt-1 bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-slate-400">Min Users</Label>
                <Input value={form.min_users} onChange={e => setForm({ ...form, min_users: e.target.value })}
                  placeholder="5, 8-10, Custom" className="mt-1 bg-slate-800 border-slate-700 text-white" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-400">Price Unit</Label>
                <Input value={form.price_unit} onChange={e => setForm({ ...form, price_unit: e.target.value })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>

            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs font-bold text-violet-400 mb-2 uppercase">Pricing (USD)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-slate-500">Monthly ($)</Label>
                  <Input type="number" value={form.price_monthly} onChange={e => setForm({ ...form, price_monthly: e.target.value })}
                    className="mt-0.5 bg-slate-900 border-slate-600 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Yearly ($)</Label>
                  <Input type="number" value={form.price_yearly} onChange={e => setForm({ ...form, price_yearly: e.target.value })}
                    className="mt-0.5 bg-slate-900 border-slate-600 text-white h-8 text-sm" />
                </div>
              </div>
            </div>

            <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs font-bold text-emerald-400 mb-2 uppercase">Pricing (INR)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-slate-500">Monthly (INR)</Label>
                  <Input type="number" value={form.price_monthly_inr} onChange={e => setForm({ ...form, price_monthly_inr: e.target.value })}
                    className="mt-0.5 bg-slate-900 border-slate-600 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Yearly (INR)</Label>
                  <Input type="number" value={form.price_yearly_inr} onChange={e => setForm({ ...form, price_yearly_inr: e.target.value })}
                    className="mt-0.5 bg-slate-900 border-slate-600 text-white h-8 text-sm" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase text-slate-400">Features (one per line)</Label>
              <textarea value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} rows={6}
                placeholder="Core ATS & CRM&#10;HireTrack Pipeline&#10;Basic Reporting"
                className="mt-1 w-full rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 font-mono"
                data-testid="plan-features-input" />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={form.highlighted} onCheckedChange={v => setForm({ ...form, highlighted: v })} />
                <Label className="text-xs text-slate-400">Highlight as "Most Popular"</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                <Label className="text-xs text-slate-400">Active</Label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500">Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                  className="mt-0.5 bg-slate-800 border-slate-700 text-white h-8 text-sm" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Max Users (-1 = unlimited)</Label>
                <Input type="number" value={form.users} onChange={e => setForm({ ...form, users: e.target.value })}
                  className="mt-0.5 bg-slate-800 border-slate-700 text-white h-8 text-sm" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Storage GB (-1 = unlimited)</Label>
                <Input type="number" value={form.storage_gb} onChange={e => setForm({ ...form, storage_gb: e.target.value })}
                  className="mt-0.5 bg-slate-800 border-slate-700 text-white h-8 text-sm" />
              </div>
            </div>

            <Button onClick={handleSave} className="w-full bg-violet-600 hover:bg-violet-500 text-white font-bold" data-testid="save-plan-btn">
              {editPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
